// Shared helpers for the XLSX exports across the app.
//
// The rule every export follows: money, counts and percentages must land in the
// sheet as *numeric* cells carrying an Excel number format — never as
// pre-formatted "$1,234" strings. A text cell can't be summed, averaged or
// charted, which is what made the old exports useless for anything beyond
// eyeballing. The "$" lives in the cell's format (`z`), not in its value.
import * as XLSX from "xlsx/xlsx.mjs";

// Excel number-format strings. The literal "$" is quoted so Excel keeps it as a
// prefix instead of applying the viewer's locale currency symbol.
export const MONEY_FMT = '"$"#,##0.00';
// Whole-peso variant, for figures the UI already rounds to the peso.
export const MONEY_FMT_0 = '"$"#,##0';
export const INTEGER_FMT = "#,##0";
export const DECIMAL_FMT = "#,##0.00";
// Fractions (0.85 → 85.0%). Excel multiplies by 100 itself.
export const PERCENT_FMT = "0.0%";
export const DATE_FMT = "dd/mm/yyyy hh:mm";
export const DATE_ONLY_FMT = "dd/mm/yyyy";

// exportType → number format, for the report framework's column declarations.
// "currency" keeps two decimals even though the on-screen table rounds to the
// peso: a sheet that displays rounded values but sums the exact ones produces
// column totals that look off by a peso.
export const EXPORT_TYPE_FMT = {
  currency: MONEY_FMT,
  currency0: MONEY_FMT_0,
  number: DECIMAL_FMT,
  integer: INTEGER_FMT,
  percent: PERCENT_FMT,
};

// "$1,234.50" / "1234.5" / 1234.5 / "" → number | null. Strips the currency
// symbol, thousands separators and stray spaces; anything that isn't a number
// after that is left alone (returns null) so text columns stay text.
export const toNumber = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v !== "string") return null;
  // Strip the currency symbol, thousands separators and (non-breaking) spaces.
  const cleaned = v.replace(/[$\s\u00A0\u202F,]/g, "").replace(/[^0-9.eE+-]/g, "");
  if (cleaned === "" || cleaned === "-" || cleaned === ".") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

// Stamp a number (or date) format on every data cell of the given columns; the
// header row is skipped. `formats` maps a 0-based column index to an Excel
// format string. Text cells holding a parseable number are converted to real
// numeric cells — that conversion is the whole point: a "$1,234" string can't
// be summed. Date cells only get the format, never a conversion.
export function applyColumnFormats(ws, formats) {
  if (!ws || !ws["!ref"]) return ws;
  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (const key of Object.keys(formats)) {
    const c = Number(key);
    const z = formats[key];
    if (!z || Number.isNaN(c) || c < range.s.c || c > range.e.c) continue;
    for (let r = range.s.r + 1; r <= range.e.r; r++) {
      const cell = ws[XLSX.utils.encode_cell({ c, r })];
      if (!cell) continue;
      if (cell.t === "n" || cell.t === "d") {
        cell.z = z;
      } else if (cell.t === "s") {
        const n = toNumber(cell.v);
        if (n === null) continue;
        cell.t = "n";
        cell.v = n;
        cell.z = z;
        delete cell.w; // drop the cached text rendering
      }
    }
  }
  return ws;
}

// Same as applyColumnFormats but keyed by header label — the convenient form
// for sheets built with XLSX.utils.json_to_sheet, where the object keys are the
// headers. Labels not present in the sheet are ignored.
export function applyHeaderFormats(ws, formatsByHeader) {
  if (!ws || !ws["!ref"]) return ws;
  const range = XLSX.utils.decode_range(ws["!ref"]);
  const byIndex = {};
  for (let c = range.s.c; c <= range.e.c; c++) {
    const h = ws[XLSX.utils.encode_cell({ c, r: range.s.r })];
    const label = h === undefined || h === null ? "" : String(h.v);
    if (Object.prototype.hasOwnProperty.call(formatsByHeader, label)) {
      byIndex[c] = formatsByHeader[label];
    }
  }
  return applyColumnFormats(ws, byIndex);
}

// Build a {header: MONEY_FMT} map from a list of header labels.
export const moneyHeaders = (labels, fmt = MONEY_FMT) =>
  Object.fromEntries(labels.map((l) => [l, fmt]));

// Timestamps arrive from PostgREST as "2026-05-16T09:00:00+00:00". Build a
// timezone-naive local Date from the wall-clock components so Excel shows the
// same day/time the table renders; parsing via new Date() would shift the hours
// by the viewer's timezone.
export const toExcelDate = (v) => {
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v !== "string") return null;
  const m = v.match(/(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (!m) return null;
  const [, y, mo, d, h, mi, se] = m;
  const dt = new Date(+y, +mo - 1, +d, +(h || 0), +(mi || 0), +(se || 0));
  return isNaN(dt.getTime()) ? null : dt;
};

// Days between the Excel epoch (1899-12-30) and the Unix epoch.
const EXCEL_EPOCH_OFFSET = 25569;

// A date/timestamp as an Excel serial number, ready to be written as a numeric
// cell carrying a date format. Handing a JS Date straight to SheetJS is NOT
// equivalent: its float conversion lands a hair *below* the true serial, so
// Excel — which truncates rather than rounds — renders 09:00 as 08:59 and, for
// a midnight date, the whole previous day. Computing the serial from the
// wall-clock components keeps whole days exact and times on the second.
export const toExcelSerial = (v) => {
  const d = toExcelDate(v);
  if (!d) return null;
  const days =
    Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86400000 +
    EXCEL_EPOCH_OFFSET;
  const secs = d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds();
  return days + secs / 86400;
};
